import { useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useSeedRosters, type SeedRostersResult } from '../queries'
import styles from './SeedRostersForm.module.css'

type SeedRostersFormProps = {
  competitionId: string
}

export function SeedRostersForm({ competitionId }: SeedRostersFormProps) {
  const [season,       setSeason]       = useState(new Date().getFullYear())
  const [leagueId,     setLeagueId]     = useState('1')
  const [forceRefresh, setForceRefresh] = useState(false)
  const [lastResult,   setLastResult]   = useState<SeedRostersResult | null>(null)

  const seed = useSeedRosters()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    seed.mutate(
      { competitionId, season, ...(leagueId.trim() ? { leagueId: leagueId.trim() } : {}), forceRefresh },
      { onSuccess: (result) => setLastResult(result) },
    )
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>3 · Importar Elencos via API-Football</h3>
      <p className={styles.hint}>
        Busca jogadores de cada time desta competição na API-Football e sincroniza{' '}
        <code>Person</code> + <code>Player</code> no banco.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Temporada</span>
            <input
              type="number"
              className={styles.input}
              value={season}
              min={2020}
              max={2030}
              onChange={(e) => setSeason(Number(e.target.value))}
              disabled={seed.isPending}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>League ID (API-Football)</span>
            <input
              type="text"
              className={styles.input}
              value={leagueId}
              placeholder="ex: 71"
              onChange={(e) => setLeagueId(e.target.value)}
              disabled={seed.isPending}
            />
          </label>
        </div>

        <label className={styles.checkboxField}>
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => setForceRefresh(e.target.checked)}
            disabled={seed.isPending}
          />
          <span>Force refresh (re-busca mesmo que já exista)</span>
        </label>

        <button
          type="submit"
          className={styles.btn}
          disabled={seed.isPending}
        >
          {seed.isPending ? (
            <span className={styles.spinner} aria-hidden="true" />
          ) : (
            <RefreshCw size={14} />
          )}
          {seed.isPending ? 'Importando…' : 'Importar Elencos'}
        </button>
      </form>

      {lastResult && <SeedResult result={lastResult} />}
    </div>
  )
}

function SeedResult({ result }: { result: SeedRostersResult }) {
  return (
    <div className={styles.result}>
      <div className={styles.resultTitle}>Último resultado</div>
      <div className={styles.stats}>
        <Stat label="Times processados"   value={result.teamsProcessed} />
        <Stat label="Times sem ExternalId" value={result.teamsSkippedNoExternalId} muted />
        <Stat label="Persons criados"     value={result.personsCreated} accent />
        <Stat label="Persons atualizados" value={result.personsRefreshed} />
        <Stat label="Persons já existiam" value={result.personsAlreadyExisted} muted />
        <Stat label="Players criados"     value={result.playersCreated} accent />
        <Stat label="Players já existiam" value={result.playersAlreadyExisted} muted />
        <Stat label="Sem posição"         value={result.playersWithoutPosition} warn={result.playersWithoutPosition > 0} />
      </div>

      {result.warnings.length > 0 && (
        <div className={styles.warnings}>
          <div className={styles.warningsHeader}>
            <AlertTriangle size={12} />
            {result.warnings.length} aviso{result.warnings.length === 1 ? '' : 's'}
          </div>
          <ul className={styles.warningsList}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat({
  label, value, accent, muted, warn,
}: {
  label:   string
  value:   number
  accent?: boolean
  muted?:  boolean
  warn?:   boolean
}) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={
        accent ? styles.statValueAccent :
        warn   ? styles.statValueWarn   :
        muted  ? styles.statValueMuted  :
        styles.statValue
      }>
        {value}
      </span>
    </div>
  )
}
